import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { useAuth } from "../contexts/AuthContext";
import { useMessageBox } from "../components/MessageBox";
import { useToast, Toast } from "../components/Toast";
import {
  getStripePublishableKey,
  getStripePricingTableId,
} from "../config/permanent-urls";
import {
  servicePlansService,
  type StripePricingTableConfig,
} from "../services/servicePlansService";
import "./ServicePlansPage.css";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "stripe-pricing-table": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        "pricing-table-id"?: string;
        "publishable-key"?: string;
        "customer-email"?: string;
        customer?: string;
      };
    }
  }
}

export const ServicePlansPage: React.FC = () => {
  const { subscription, isLoadingSubscription, reloadSubscription } =
    useSubscriptionContext();
  const { profile } = useAuth();
  const { showMessageBox } = useMessageBox();
  const { toast, showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
  const [stripePricingConfig, setStripePricingConfig] =
    useState<StripePricingTableConfig | null>(null);
  const [stripePricingConfigError, setStripePricingConfigError] = useState<
    string | null
  >(null);

  // Check for Stripe redirect parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    const success = params.get("success");
    const canceled = params.get("canceled");

    if (sessionId && success === "true") {
      // Payment successful - show success message and refresh subscription
      setShowSuccessMessage(true);
      reloadSubscription();

      // Remove query parameters from URL
      navigate("/service-plans", { replace: true });

      // Hide success message after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

      return () => clearTimeout(timer);
    } else if (canceled === "true") {
      // Payment canceled - just remove query parameters
      navigate("/service-plans", { replace: true });
    }
  }, [location.search, navigate, reloadSubscription]);

  // Load runtime Stripe pricing-table config from the deployed backend environment.
  useEffect(() => {
    let cancelled = false;

    const loadStripePricingConfig = async () => {
      try {
        const runtimeConfig =
          await servicePlansService.getStripePricingTableConfig();
        if (cancelled) return;
        setStripePricingConfig(runtimeConfig);
        setStripePricingConfigError(null);
        return;
      } catch (runtimeError) {
        // Backward-compatible fallback for local/dev setups still using VITE_*.
        try {
          const fallbackConfig: StripePricingTableConfig = {
            pricingTableId: getStripePricingTableId(),
            publishableKey: getStripePublishableKey(),
            source: "vite-env-fallback",
          };
          if (cancelled) return;
          setStripePricingConfig(fallbackConfig);
          setStripePricingConfigError(null);
          return;
        } catch (fallbackError) {
          if (cancelled) return;
          const runtimeMessage =
            runtimeError instanceof Error
              ? runtimeError.message
              : String(runtimeError);
          const fallbackMessage =
            fallbackError instanceof Error
              ? fallbackError.message
              : String(fallbackError);
          setStripePricingConfig(null);
          setStripePricingConfigError(
            `Unable to load Stripe pricing table configuration. Runtime: ${runtimeMessage}. Fallback: ${fallbackMessage}.`,
          );
        }
      }
    };

    void loadStripePricingConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  // Detect if running in iframe
  useEffect(() => {
    const checkIframe = () => {
      try {
        // Check if we're in an iframe by comparing window.self to window.top
        const inIframe = window.self !== window.top;
        setIsInIframe(inIframe);

        if (inIframe) {
          // Iframe context detected
        }
      } catch (e) {
        // If we can't access window.top, we're likely in a cross-origin iframe
        // In that case, assume we're in an iframe
        setIsInIframe(true);
      }
    };

    checkIframe();
  }, []);

  // Load Stripe pricing table script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const existingScript = document.querySelector(
        'script[src="https://js.stripe.com/v3/pricing-table.js"]',
      );
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  // Handle Stripe checkout navigation when in iframe
  // The issue: Stripe pricing table tries to navigate parent window, which is blocked by sandbox
  // Solution: Intercept navigation attempts and open checkout in new window
  useEffect(() => {
    if (!isInIframe) return;

    // Store original functions
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    // Override pushState to intercept Stripe navigation
    history.pushState = function (state, title, url?: string | URL | null) {
      if (url) {
        const urlString = typeof url === "string" ? url : url.toString();
        if (
          urlString.includes("checkout.stripe.com") ||
          urlString.includes("stripe.com/checkout")
        ) {
          const newWindow = window.open(
            urlString,
            "_blank",
            "noopener,noreferrer",
          );
          if (!newWindow) {
            showToast({
              type: "error",
              message: "Popup Blocked",
              description:
                "Please allow popups for this site to complete checkout. The checkout window was blocked.",
              duration: 6000,
            });
          }
          return;
        }
      }
      return originalPushState(state, title, url);
    };

    history.replaceState = function (state, title, url?: string | URL | null) {
      if (url) {
        const urlString = typeof url === "string" ? url : url.toString();
        if (
          urlString.includes("checkout.stripe.com") ||
          urlString.includes("stripe.com/checkout")
        ) {
          const newWindow = window.open(
            urlString,
            "_blank",
            "noopener,noreferrer",
          );
          if (!newWindow) {
            showToast({
              type: "error",
              message: "Popup Blocked",
              description:
                "Please allow popups for this site to complete checkout. The checkout window was blocked.",
              duration: 6000,
            });
          }
          return;
        }
      }
      return originalReplaceState(state, title, url);
    };

    // Intercept window.location.href changes
    let locationHrefDescriptor: PropertyDescriptor | undefined;
    try {
      const locationProto = Object.getPrototypeOf(window.location);
      locationHrefDescriptor = Object.getOwnPropertyDescriptor(
        locationProto,
        "href",
      );

      if (locationHrefDescriptor && locationHrefDescriptor.set) {
        const originalHrefSetter = locationHrefDescriptor.set;
        Object.defineProperty(window.location, "href", {
          set: function (url: string) {
            if (
              url &&
              (url.includes("checkout.stripe.com") ||
                url.includes("stripe.com/checkout"))
            ) {
              const newWindow = window.open(
                url,
                "_blank",
                "noopener,noreferrer",
              );
              if (!newWindow) {
                showToast({
                  type: "error",
                  message: "Popup Blocked",
                  description:
                    "Please allow popups for this site to complete checkout. The checkout window was blocked.",
                  duration: 6000,
                });
              }
              return;
            }
            return originalHrefSetter.call(window.location, url);
          },
          get: locationHrefDescriptor.get,
          configurable: true,
          enumerable: true,
        });
      }
    } catch (e) {
      // Could not intercept window.location.href (may be non-configurable)
    }

    // Listen for postMessage events from Stripe pricing table
    const handleMessage = (event: MessageEvent) => {
      // Only process messages from Stripe domains
      if (
        !event.origin.includes("stripe.com") &&
        !event.origin.includes("js.stripe.com")
      ) {
        return;
      }

      if (event.data && typeof event.data === "object") {
        const data = event.data;

        // Check for Stripe checkout URLs
        if (
          data.url &&
          (data.url.includes("checkout.stripe.com") ||
            data.url.includes("stripe.com/checkout"))
        ) {
          const newWindow = window.open(
            data.url,
            "_blank",
            "noopener,noreferrer",
          );
          if (!newWindow) {
            showToast({
              type: "error",
              message: "Popup Blocked",
              description:
                "Please allow popups for this site to complete checkout. The checkout window was blocked.",
              duration: 6000,
            });
          }
          event.preventDefault?.();
          event.stopPropagation?.();
          return;
        }

        // Check for Stripe checkout session created
        if (data.type === "checkout.session.created" || data.sessionId) {
          // Notify parent frame to handle navigation (if they have a handler)
          try {
            if (window.parent && window.parent !== window.self) {
              window.parent.postMessage(
                {
                  type: "stripe-checkout-started",
                  sessionId: data.sessionId || data.id,
                  source: "kairos-app",
                  action: "open-checkout",
                  message:
                    "Stripe checkout initiated - please open in new window if navigation fails",
                },
                "*",
              ); // Parent should validate origin
            }
          } catch (e) {
            // Failed to notify parent frame
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Also try to intercept clicks on Stripe pricing table buttons
    // This is a fallback in case navigation interception doesn't work
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const pricingTable = document.querySelector("stripe-pricing-table");

      if (pricingTable && pricingTable.contains(target)) {
        // Check if this is a subscribe/checkout button
        const button =
          target.closest("button") ||
          target.closest('[role="button"]') ||
          target;
        const buttonText = button.textContent?.toLowerCase() || "";

        if (
          buttonText.includes("subscribe") ||
          buttonText.includes("checkout") ||
          buttonText.includes("select")
        ) {
          // Don't prevent default - let Stripe handle it
        }
      }
    };

    // Use capture phase to catch events early
    document.addEventListener("click", handleClick, true);

    // Cleanup
    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;

      if (locationHrefDescriptor && locationHrefDescriptor.set) {
        try {
          Object.defineProperty(
            window.location,
            "href",
            locationHrefDescriptor,
          );
        } catch (e) {
          // Might not be able to restore, that's OK
        }
      }

      window.removeEventListener("message", handleMessage);
      document.removeEventListener("click", handleClick, true);
    };
  }, [isInIframe]);

  // Get customer information for Stripe pricing table
  // CRITICAL: Always use the Cognito profile email to ensure consistency
  // Prefer customer ID if available (links to existing Stripe customer and enforces email)
  // Otherwise use email from profile to pre-fill and enforce the same email
  const customerId = subscription?.stripeCustomerId || undefined;
  // Always use the authenticated user's email from Cognito profile
  // Ensure email is never null - convert null/empty to undefined to prevent Stripe errors
  const customerEmail =
    profile?.email && profile.email.trim() ? profile.email.trim() : undefined;
  const subscriptionEndDate =
    subscription?.stripeCurrentPeriodEnd ||
    subscription?.nextBillingDate ||
    null;
  const isCancellationScheduled = Boolean(
    subscription?.stripeCancelAtPeriodEnd,
  );
  const canCancelSubscription =
    Boolean(subscription?.stripeSubscriptionId) &&
    subscription?.status === "active";

  const formatSubscriptionDate = (dateValue: string | null): string => {
    if (!dateValue) return "the end of your billing period";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return "the end of your billing period";
    }
    return parsed.toLocaleDateString("en-US", { timeZone: "UTC" });
  };

  const performCancelSubscription = async () => {
    if (!subscription?.stripeSubscriptionId) {
      return;
    }

    try {
      setIsCancelingSubscription(true);
      await servicePlansService.cancelSubscription();
      await reloadSubscription(false, true);
      showToast({
        type: "success",
        message: "Cancellation Scheduled",
        description: `Your plan will remain active until ${formatSubscriptionDate(subscriptionEndDate)}.`,
        duration: 6000,
      });
    } catch (error: any) {
      showMessageBox({
        type: "error",
        title: "Cancellation Failed",
        message:
          error?.message || "Failed to cancel subscription. Please try again.",
        buttons: ["ok"],
        onButtonClick: () => {},
      });
    } finally {
      setIsCancelingSubscription(false);
    }
  };

  const handleCancelSubscription = () => {
    if (!canCancelSubscription || isCancelingSubscription) {
      return;
    }

    const endDateLabel = formatSubscriptionDate(subscriptionEndDate);
    showMessageBox({
      type: "confirm",
      title: "Cancel subscription?",
      message: `This stops future charges. Your plan stays active until ${endDateLabel}. No refunds or prorations.`,
      buttons: ["cancel", "yes"],
      buttonLabels: {
        cancel: "Keep Subscription",
        yes: "Cancel Subscription",
      },
      onButtonClick: (button) => {
        if (button === "yes") {
          void performCancelSubscription();
        }
      },
    });
  };

  return (
    <div className="service-plans-page">
      <div className="service-plans-container">
        <div className="service-plans-header">
          <div className="service-plans-header-content">
            <h1 className="service-plans-title">Token Packages</h1>
            <p className="service-plans-subtitle">
              Purchase tokens to power your job search
            </p>
          </div>
        </div>

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="subscription-success-message">
            <div className="success-content">
              <span className="success-icon">✅</span>
              <div className="success-text">
                <h3>Payment Successful!</h3>
                <p>Your tokens have been added to your balance.</p>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Pricing Table (token packs configured in Stripe Dashboard) */}
        {profile?.email ? (
          <div className="stripe-pricing-table-container">
            {isInIframe && (
              <div className="iframe-checkout-notice">
                For security reasons, checkout may open in a new window. Please
                allow popups for this site if prompted by your browser.
              </div>
            )}
            {stripePricingConfigError ? (
              <div className="service-plans-auth-required">
                <div className="auth-required-message">
                  <h3>Pricing Unavailable</h3>
                  <p>{stripePricingConfigError}</p>
                </div>
              </div>
            ) : !stripePricingConfig ? (
              <div className="service-plans-auth-required">
                <div className="auth-required-message">
                  <h3>Loading pricing…</h3>
                  <p>Please wait while we load the payment configuration.</p>
                </div>
              </div>
            ) : (
              <>
                {/* @ts-expect-error - stripe-pricing-table is a custom web component */}
                <stripe-pricing-table
                  pricing-table-id={stripePricingConfig.pricingTableId}
                  publishable-key={stripePricingConfig.publishableKey}
                  {...(customerId
                    ? { customer: customerId }
                    : customerEmail
                      ? { "customer-email": customerEmail }
                      : {})}
                />
              </>
            )}
          </div>
        ) : (
          <div className="service-plans-auth-required">
            <div className="auth-required-message">
              <h3>Authentication Required</h3>
              <p>Please sign in to purchase tokens.</p>
              <p className="auth-required-note">
                Use the same email address as your Kairos account.
              </p>
            </div>
          </div>
        )}

        {/* Wallet / optional legacy Stripe subscription */}
        {subscription && !isLoadingSubscription && (
          <div className="subscription-info">
            <h3>Your wallet</h3>
            <div className="subscription-row">
              <div className="subscription-details">
                <p>
                  <strong>Tokens remaining:</strong>{" "}
                  {typeof subscription.tokensRemaining === "number"
                    ? subscription.tokensRemaining.toLocaleString()
                    : "—"}
                </p>
                {subscription.createdAt && (
                  <p>
                    <strong>Account since:</strong>{" "}
                    {new Date(subscription.createdAt).toLocaleDateString(
                      "en-US",
                      {
                        timeZone: "UTC",
                      },
                    )}
                  </p>
                )}
                {isCancellationScheduled && (
                  <p className="subscription-cancel-note">
                    <strong>Cancellation scheduled:</strong> access ends on{" "}
                    {formatSubscriptionDate(subscriptionEndDate)}.
                  </p>
                )}
              </div>
              {canCancelSubscription && (
                <div className="subscription-actions">
                  <button
                    className="cancel-subscription-button"
                    onClick={handleCancelSubscription}
                    disabled={
                      isCancelingSubscription || isCancellationScheduled
                    }
                  >
                    {isCancelingSubscription
                      ? "Canceling..."
                      : isCancellationScheduled
                        ? "Cancellation scheduled"
                        : "Cancel recurring billing"}
                  </button>
                  <p className="cancel-subscription-disclaimer">
                    Only shown if you still have an old recurring Stripe
                    subscription. Token packs are one-time purchases.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Toast {...toast} />
    </div>
  );
};
